<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { useEditorBetaStore } from '@/stores/editor/beta'
import { useLayoutStore } from '@/stores/layout'

const props = defineProps<{
    element: any
    extra: any
    elementIndex: number
    sectionIndex: number
    isNew?: boolean
    isNewBefore?: boolean
    schema?: any
}>()

const betaStore = useEditorBetaStore()
const layoutStore = useLayoutStore()

const content = computed(() => props.element.frameContent ?? {})
const text = ref(content.value?.text ?? '')
const hasExtra = computed(() => props.extra != null)

watch(
    () => props.element.frameContent?.text,
    value => {
        text.value = value ?? ''
    },
)

const dynamicStyle = computed(() => {
    if (!hasExtra.value) return undefined

    const style: Record<string, string> = {
        margin: `${props.extra.margin?.top ?? 0}px ${props.extra.margin?.right ?? 0}px ${props.extra.margin?.bottom ?? 0}px ${props.extra.margin?.left ?? 0}px`,
        padding: `${props.extra.padding?.top ?? 0}px ${props.extra.padding?.right ?? 0}px ${props.extra.padding?.bottom ?? 0}px ${props.extra.padding?.left ?? 0}px`,
        color: props.extra.style?.color || layoutStore.getButtonColor(props.sectionIndex),
        backgroundColor: props.extra.style?.backgroundColor || layoutStore.getButtonBackgroundColor(props.sectionIndex),
        maxWidth: '100%',
    }

    if (!props.extra.style_border_radius_lock) style.borderRadius = props.extra.style?.borderRadius
    if (!props.extra.style_font_size_lock) style.fontSize = props.extra.style?.fontSize
    if (!props.extra.style_font_weight_lock) style.fontWeight = props.extra.style?.fontWeight

    return style
})

const dynamicStyleAlign = computed(() => {
    if (!hasExtra.value) return undefined

    const textAlign = props.extra.style?.textAlign ?? 'center'
    const style: Record<string, string> = {}
    const alignMap: Record<string, string> = {
        left: 'flex-start',
        center: 'center',
        right: 'flex-end',
    }

    style.textAlign = textAlign
    style.justifyContent = alignMap[textAlign] ?? textAlign

    return style
})

const onChangeButtonText = () => {
    betaStore.patchElementBySchema(props.schema, {
        frameContent: {
            text: text.value,
        },
    })
}
</script>

<template>
    <div
        tabindex="0"
        :id="'betaEditorButton-' + elementIndex"
        class="editor-button-beta"
        :style="dynamicStyleAlign"
        :schema="JSON.stringify(schema)"
    >
        <button class="button-element" :style="dynamicStyle" type="button">
            <input
                v-model="text"
                type="text"
                class="input-element-button appearance-none bg-transparent text-center outline-none"
                @input="onChangeButtonText"
                @pointerdown.stop
            >
        </button>
    </div>
</template>

<style scoped>
.editor-button-beta {
    @apply relative block min-w-0 max-w-full border-2 border-transparent bg-transparent outline-none transition-all duration-1000 ease-in-out hover:border-[#D7E3F4] hover:shadow-[0_0_0_2px_rgba(215,227,244,0.22)];
    box-sizing: border-box;
    width: 100%;
}

.editor-button-beta .button-element {
    @apply inline-flex min-w-0 max-w-full items-center justify-center overflow-hidden;
    box-sizing: border-box;
}

.editor-button-beta .input-element-button {
    @apply min-w-0 max-w-full;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
